/* eslint-disable @typescript-eslint/no-explicit-any */
import { mapToolResponse } from '@/mappers/tool.mapper';

import type {
  Agent,
  AgentDetailResponse,
  AgentKnowledgeBase,
  AgentKnowledgeBaseDocument,
  AgentTemplate,
  AgentTool,
  AgentUpdateResponseSchema,
  Voice,
} from '@/types/agent.type';
import type { DropdownOption } from '@/types/common.type';

export const mapAgentResponse = (data: Record<string, any>): Agent => {
  return {
    id: data.id,
    canonical_name: data.canonical_name,
    agent_name: data.agent_name,
    voice: {
      id: data.voice.id.toString(),
      voice_id: data.voice.voice_id,
      voice_name: data.voice.name,
      gender: data.voice.gender,
      language: data.voice.language,
    },
    temperature: data.temperature,
    status: data.status,
    created_at: data.created_at,
  };
};

export const mapAgentCreateResponse = (response: Record<string, any>): string => {
  return response.data.canonical_name;
};

export const mapAgentDetailResponse = (response: Record<string, any>): AgentDetailResponse => {
  const data = response.data;
  return {
    id: data.id,
    canonical_name: data.canonical_name,
    can_publish: data.can_publish,
    agent_name: data.agent_name,
    temperature: data.temperature,
    status: data.status,
    created_at: data.created_at,
    agent_description: data.description,
    agent_phone_number: data.agent_phone_number,
    has_telephony_configured: data.has_telephony_configured,

    voice: {
      id: data.voice.id,
      voice_id: data.voice.voice_id,
      voice_name: data.voice.name,
      gender: data.voice.gender,
      language: data.voice.language,
    },

    call_settings: {
      ring_duration: data.call_settings?.ring_duration,
      max_call_duration: data.call_settings?.max_call_duration,
      end_call_on_silence: data.call_settings?.end_call_on_silence,
      webhook_config: {
        url: data.call_settings?.webhook_config?.url,
        headers: data.call_settings?.webhook_config?.headers || [],
        query_params: data.call_settings?.webhook_config?.query_params || [],
        timeout: data.call_settings?.webhook_config?.timeout_in_milliseconds,
        events: data.call_settings?.webhook_config?.events || [],
      },
      is_call_recording_enabled: data.call_settings?.is_call_recording_enabled,
    },

    system_prompt: {
      prompt: data.system_prompt?.prompt,
      variables: data.system_prompt?.variables || [],
    },

    summary: data.post_call_prompts?.find(
      (prompt: Record<string, any>) => prompt.type === 'Summary'
    ),
    data_extraction: data.post_call_prompts?.find(
      (prompt: Record<string, any>) => prompt.type === 'Extraction'
    ),
    sentiment: data.post_call_prompts?.find(
      (prompt: Record<string, any>) => prompt.type === 'Sentiment'
    ),
  };
};

export const mapVoiceResponse = (data: { data: Array<Record<string, any>> }): Voice[] => {
  return data.data.map((voice: Record<string, any>) => ({
    id: voice.id,
    voice_id: voice.voice_id,
    voice_name: voice.name,
    language: voice.language,
    gender: voice.gender,
  }));
};

export const mapLLMResponse = (data: { data: Array<Record<string, any>> }): DropdownOption[] => {
  return data.data.map((item: Record<string, any>) => ({
    value: item.value,
    label: item.label,
  }));
};

export const mapAgentUpdateResponse = (
  response: Record<string, any>
): AgentUpdateResponseSchema => {
  const data = response.data;

  return {
    id: data.id,
    canonical_name: data.canonical_name,
  };
};

export const mapDropdownOptions = (
  data: Record<string, Array<Record<string, string>>>
): DropdownOption[] => {
  return data.data.map((item: Record<string, any>) => ({
    value: item.value,
    label: item.label,
  }));
};

export const mapAgentKnowledgeBases = (response: Record<string, any>): AgentKnowledgeBase[] => {
  const list: Array<Record<string, any>> = Array.isArray(response)
    ? response
    : (response?.data ?? []);

  return list.map(
    (kb): AgentKnowledgeBase => ({
      id: kb.id,
      name: kb.name,
      kb_description: kb.kb_description,
      is_linked: kb.is_linked,
      ...(kb.documents && {
        documents: kb.documents.map(
          (doc: Record<string, any>): AgentKnowledgeBaseDocument => ({
            file_path: doc.file_path,
            file_name: doc.file_name,
            file_type: doc.file_type,
            file_size: doc.file_size,
            updated_at: doc.updated_at,
          })
        ),
      }),
    })
  );
};

export const mapAgentTools = (response: Record<string, any>): AgentTool[] => {
  const list: Array<Record<string, any>> = Array.isArray(response)
    ? response
    : (response?.data ?? []);

  return list.map(
    (tool): AgentTool => ({
      ...mapToolResponse(tool),
      is_linked: Boolean(tool.is_linked),
    })
  );
};

export const mapAgentTemplates = (response: Record<string, any>): AgentTemplate[] => {
  const list: Array<Record<string, any>> = Array.isArray(response)
    ? response
    : (response?.data ?? []);

  return list.map(
    (template): AgentTemplate => ({
      id: template.id,
      name: template.template_name,
      prompt: template.prompt,
      description: template.description,
    })
  );
};
